import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, MenuItem, TextField, Typography } from '@mui/material';
import {
  confirmDealerSubscription,
  getDealerSubscription,
  previewDealerSubscription,
} from '../../api/backendApi';

const EMPTY_FEATURES = [];

const STATUS_LABELS = {
  not_configured: 'Not Configured',
  confirmed: 'Confirmed',
  scheduled: 'Scheduled',
  canceled: 'Canceled',
};

function wholesaleFor(feature, currency) {
  return currency === 'USD' ? feature.wholesaleUsd : feature.wholesaleCad;
}

function moneyLabel(feature) {
  const unit = feature.billingUnit || 'unit';
  if (feature.billingMethod === 'monthly_fixed') return `per ${unit} / month`;
  if (feature.billingMethod === 'per_unit_monthly') return `per ${unit} / month`;
  if (feature.billingMethod === 'included_overage') return `per ${unit}; overage after the included allowance`;
  return `per ${unit}`;
}

export default function CustomerSubscriptionSection({ dealerId, tenantId }) {
  const [record, setRecord] = useState(null);
  const [currency, setCurrency] = useState('CAD');
  const [selected, setSelected] = useState({});
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!dealerId || !tenantId) return;
    getDealerSubscription(dealerId, tenantId)
      .then((response) => {
        const data = response?.data || {};
        setRecord(data);
        const active = data.current || data.scheduled;
        if (active?.currency) setCurrency(active.currency);
        const next = {};
        (active?.features || []).forEach((feature) => {
          next[feature.featureId] = {
            retailPrice: feature.retailPrice || '',
            retailOverage: feature.retailOverage || '',
            catalogVersion: feature.catalogVersion,
          };
        });
        setSelected(next);
        setError('');
      })
      .catch(() => setError('The subscription could not be loaded.'));
  };

  useEffect(() => {
    load();
  }, [dealerId, tenantId]);

  const features = record?.catalog || EMPTY_FEATURES;
  const selectionKey = useMemo(() => JSON.stringify({ currency, selected }), [currency, selected]);

  useEffect(() => {
    const chosen = features.filter((feature) => selected[feature.featureId]?.retailPrice);
    if (!dealerId || !tenantId || !chosen.length) {
      setPreview(null);
      return undefined;
    }
    let canceled = false;
    previewDealerSubscription(dealerId, tenantId, {
      currency,
      expectedVersion: record?.subscriptionVersion || 0,
      features: chosen.map((feature) => ({
        featureId: feature.featureId,
        retailPrice: selected[feature.featureId].retailPrice,
        catalogVersion: feature.catalogVersion,
        ...(feature.billingMethod === 'included_overage'
          ? { retailOverage: selected[feature.featureId].retailOverage || '' }
          : {}),
      })),
    })
      .then((response) => {
        if (!canceled) {
          setPreview(response?.data || null);
          setError('');
        }
      })
      .catch((err) => {
        if (!canceled) {
          setPreview(null);
          setError(err?.message || 'The margin could not be calculated.');
        }
      });
    return () => {
      canceled = true;
    };
  }, [selectionKey, dealerId, tenantId, features, record?.subscriptionVersion]);

  const toggle = (feature) => {
    setSelected((current) => {
      const next = { ...current };
      if (next[feature.featureId]) delete next[feature.featureId];
      else next[feature.featureId] = { retailPrice: '', retailOverage: '', catalogVersion: feature.catalogVersion };
      return next;
    });
  };

  const edit = (featureId, field, value) => {
    setSelected((current) => ({
      ...current,
      [featureId]: { ...current[featureId], [field]: value },
    }));
  };

  const save = async (cancel) => {
    setSaving(true);
    setError('');
    const body = cancel
      ? { cancel: true, expectedVersion: record?.subscriptionVersion || 0 }
      : preview && {
          currency,
          expectedVersion: record?.subscriptionVersion || 0,
          features: (preview.features || []).map((feature) => ({
            featureId: feature.featureId,
            retailPrice: feature.retailPrice,
            catalogVersion: feature.catalogVersion,
            ...(feature.retailOverage ? { retailOverage: feature.retailOverage } : {}),
          })),
        };
    if (!body) {
      setSaving(false);
      setError('Enter a retail price and review the margin before confirming.');
      return;
    }
    try {
      await confirmDealerSubscription(dealerId, tenantId, body);
      load();
    } catch (err) {
      setError(err?.message || 'The subscription was not saved.');
    } finally {
      setSaving(false);
    }
  };

  const marginFor = (featureId) => (preview?.features || []).find((feature) => feature.featureId === featureId);

  return (
    <Box sx={{ display: 'grid', gap: 1.5, mt: 3 }}>
      <Typography variant="h5">Customer Subscription</Typography>
      <Typography>
        Status: {STATUS_LABELS[record?.status] || 'Not Configured'}
        {record?.currency ? ` · ${record.currency}` : ''}
      </Typography>
      <Typography>
        Saving records the commercial agreement. It does not change this customer&apos;s current feature access.
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <TextField
        select
        label="Currency"
        value={currency}
        onChange={(event) => setCurrency(event.target.value)}
        sx={{ maxWidth: 240 }}
      >
        <MenuItem value="CAD">CAD</MenuItem>
        <MenuItem value="USD">USD</MenuItem>
      </TextField>
      {features.map((feature) => {
        const choice = selected[feature.featureId];
        const margin = marginFor(feature.featureId);
        return (
          <Box key={feature.featureId} sx={{ display: 'grid', gap: 1, p: 1.5, border: '1px solid #e5e7eb', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox checked={Boolean(choice)} onChange={() => toggle(feature)} inputProps={{ 'aria-label': feature.name }} />
              <Box>
                <Typography>{feature.name}</Typography>
                <Typography variant="body2">{feature.description}</Typography>
                <Typography variant="body2">{moneyLabel(feature)}</Typography>
                {feature.allowance ? <Typography variant="body2">Included allowance: {feature.allowance}</Typography> : null}
              </Box>
            </Box>
            <Typography>Wholesale ({currency}): {wholesaleFor(feature, currency) || '—'}</Typography>
            {choice ? (
              <TextField
                label={`Retail price (${currency})`}
                value={choice.retailPrice}
                onChange={(event) => edit(feature.featureId, 'retailPrice', event.target.value)}
                sx={{ maxWidth: 280 }}
              />
            ) : null}
            {choice && feature.billingMethod === 'included_overage' ? (
              <TextField
                label={`Retail overage (${currency})`}
                value={choice.retailOverage}
                onChange={(event) => edit(feature.featureId, 'retailOverage', event.target.value)}
                sx={{ maxWidth: 280 }}
              />
            ) : null}
            {margin ? (
              <Typography>
                Estimated margin: {margin.marginAmount} {currency}
                {margin.marginPercent ? ` (${margin.marginPercent}%)` : ''}
              </Typography>
            ) : null}
          </Box>
        );
      })}
      {record?.commercialBeta || preview?.commercialBeta ? (
        <Alert severity="info">{record?.label || preview?.label || 'BETA ESTIMATE — NOT BILLABLE'}. No amount is payable.</Alert>
      ) : null}
      {preview ? (
        <Box>
          <Typography variant="h6">Estimated margins</Typography>
          <Typography>Monthly recurring retail: {preview.monthlyRecurringRetail} {currency}</Typography>
          <Typography>Monthly recurring wholesale: {preview.monthlyRecurringWholesale} {currency}</Typography>
          <Typography>Monthly recurring margin: {preview.monthlyRecurringMargin} {currency}</Typography>
          <Typography>{preview.usageNote}</Typography>
        </Box>
      ) : null}
      {record?.current ? (
        <Box>
          <Typography variant="h6">Current agreement</Typography>
          <Typography>Effective {record.current.effectiveFrom} · {record.current.currency}</Typography>
          {(record.current.features || []).map((feature) => (
            <Typography key={feature.featureId}>
              {feature.name}: retail {feature.retailPrice}, wholesale {wholesaleFor(feature, record.current.currency)}
            </Typography>
          ))}
        </Box>
      ) : null}
      {record?.scheduled ? (
        <Typography>A change is scheduled for {record.scheduled.effectiveFrom}.</Typography>
      ) : null}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button variant="contained" disabled={saving || !preview} onClick={() => save(false)}>Confirm subscription</Button>
        {record?.current ? (
          <Button disabled={saving} onClick={() => save(true)}>Cancel subscription</Button>
        ) : null}
      </Box>
    </Box>
  );
}
